import { BORDER, WHITE } from 'src/constants/colors';
import {
    Measurement,
    MeasurementData,
    MeasurementStatistic,
    Network,
} from '@nne-viz/common';
import { Method, useApi } from 'src/services/api';
import NetworkGraphLineData, {
    measurementToNetworkLineGraph,
} from 'src/models/NetworkGraphLineData';
import { NorwayDayjs, norwaydayjsToUrlIso8601 } from 'src/utils/dates';
import { Point, ResponsiveLine } from '@nivo/line';
import React, { useEffect, useState } from 'react';
import SquareLoader, {
    SquareLoaderContainer,
} from 'src/components/loader/SquareLoader';

import { measureUnit } from 'src/utils/math';
import styled from 'styled-components';
import { translateMeasurement } from 'src/utils/translate';

/**
 * @description container for the plot
 */
const PlotContainer = styled.div`
    height: 95%;
    width: 95%;
`;
/**
 * @description container for the tooltip when hover data in plot
 */
const TooltipContainer = styled.div`
    background: ${WHITE};
    border: solid;
    border-color: ${BORDER};
    border-radius: 7px;
    border-width: 1px;
    padding: 0.05rem 0.5rem;
`;
/**
 * @description props for network graph component
 * @type {Props}
 */

interface Props {
    /**
     * @description is component loading
     * @type {boolean}
     */
    isLoading: boolean;
    /**
     * @description from date to display data from
     * @type {NorwayDayjs}
     */
    from: NorwayDayjs;
    /**
     * @description to date to display data to
     * @type {NorwayDayjs}
     */
    to: NorwayDayjs;
    /**
     * @description measurement to display
     * @type {Measurement}
     */
    measurement: Measurement;
    /**
     * @description networks
     * @type {Network[]}
     */
    networks: Network[];
    /**
     * @description statistic displayed
     * @type {MeasurementStatistic}
     */
    statistic: MeasurementStatistic;
}

/**
 * @description network item component for displaying network graph
 * @type {React.FC}
 */
const NetworkLineGraph: React.FC<Props> = ({
    networks,
    isLoading,
    from,
    to,
    measurement,
    statistic,
}: Props) => {
    // data to display
    const [data, setData] = useState<NetworkGraphLineData[]>([]);
    // fetch data from backend
    const [{ data: fetchData, isLoading: _isLoading }, doFetch] = useApi<
        MeasurementData[]
    >({
        method: Method.POST,
        isAuthorizationNeeded: true,
        initialData: [],
        initialEndpoint: `network/measurement/${measurement}/${statistic}?from=${norwaydayjsToUrlIso8601(
            from
        )}&to=${norwaydayjsToUrlIso8601(to)}`,
        initialBody: { networks_id: networks.map((network) => network.id) },
    });
    // useEffect run when component is first rendered
    useEffect(() => {
        doFetch({
            endpoint: `network/measurement/${measurement}/${statistic}?from=${norwaydayjsToUrlIso8601(
                from
            )}&to=${norwaydayjsToUrlIso8601(to)}`,
            body: { networks_id: networks.map((network) => network.id) },
        });
    }, [from, to, networks, measurement]);
    // useEffect to convert data when fetched to displayable data
    useEffect(() => {
        setData(measurementToNetworkLineGraph(fetchData));
    }, [fetchData]);

    /**

     * @function tooltip
     * @description read https://nivo.rocks/swarmplot/ for tooltip description
     * create the div for displaying data when mouse is hover a data
     * @param {{point: Point}} node
     * @returns {JSX.Element}
     */

    const tooltip = ({ point }: { point: Point }): JSX.Element => {
        // format value for only 3 decimals after
        return (
            <TooltipContainer>
                <p>
                    <strong>
                        {point.data.xFormatted} {point.serieId}
                    </strong>
                </p>
                <p>
                    {translateMeasurement(measurement)}: {point.data.yFormatted}{' '}
                    {measureUnit(measurement)}
                </p>
            </TooltipContainer>
        );
    };
    /**
     * @function yScaleMax
     * @param {NetworkGraphLineData[]} data the data to display
     * @param {Measurement} measurement the measurement
     * @returns {number} the maximum value for y-axis
     */
    const yScaleMax = (
        data: NetworkGraphLineData[],
        measurement: Measurement
    ): number => {
        // get the maximum value on Y axis
        const max: number = Math.max(
            ...data.reduce((acc: number[], line: NetworkGraphLineData) => {
                return [...acc, ...line.data.map((point) => point.y)];
            }, [])
        );
        switch (measurement) {
            case Measurement.PACKET_LOSS:
                return max > 99 ? 100 : max + 1;
            default:
                return max + 5;
        }
    };

    return _isLoading || isLoading ? (
        <SquareLoaderContainer>
            <SquareLoader />
        </SquareLoaderContainer>
    ) : (
        <PlotContainer>
            <ResponsiveLine
                axisBottom={{ tickSize: 50, legend: 'Measurements\' days' }}
                axisLeft={{
                    tickSize: 50,
                }}
                axisRight={null}
                axisTop={null}
                colors={(data) => data.color}
                data={data}
                legends={[
                    {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: 0,
                        itemWidth: 50,
                        itemHeight: 20,
                        itemsSpacing: 4,
                        symbolSize: 15,
                        symbolShape: 'circle',
                        itemDirection: 'left-to-right',
                    },
                ]}
                lineWidth={3}
                margin={{ top: 90, right: 65, bottom: 65, left: 82 }}
                pointSize={12}
                tooltip={tooltip}
                useMesh={true}
                yFormat={'>-.3f'}
                yScale={{
                    type: 'linear',
                    stacked: false,
                    min: 0,
                    max: yScaleMax(data, measurement),
                }}
            ></ResponsiveLine>
        </PlotContainer>
    );
};

export default NetworkLineGraph;
